import { create } from 'zustand';
import type { Organization, Application } from '../types';
import {
  getOrganizations,
  getOrganization,
  createOrganization,
  updateOrganization,
  getApplications,
  getApplication,
  createApplication,
  updateApplication,
} from '../services/api';

interface AppState {
  organizations: Organization[];
  applications: Application[];
  currentOrg: Organization | null;
  currentApp: Application | null;
  loading: boolean;
  error: string | null;

  // Cache state
  organizationsLoaded: boolean;
  organizationsCacheTime: number;

  // Organization Actions
  fetchOrganizations: (force?: boolean) => Promise<void>;
  fetchOrganization: (id: string) => Promise<Organization>;
  createOrg: (data: { name: string; domain?: string }) => Promise<Organization>;
  updateOrg: (id: string, data: { name?: string; domain?: string }) => Promise<Organization>;

  // Application Actions
  fetchApplications: (orgId?: string) => Promise<void>;
  fetchApplication: (orgId: string, appId: string) => Promise<Application>;
  createApp: (orgId: string, data: {
    name: string;
    redirectUris: string[];
    appUrl?: string;
    logoUrl?: string;
    logUrl?: string;
    scope?: string[];
  }) => Promise<Application>;
  updateApp: (orgId: string, appId: string, data: {
    name?: string;
    redirectUris?: string[];
    appUrl?: string;
    logoUrl?: string;
    logUrl?: string;
    scope?: string[];
    isActive?: boolean;
  }) => Promise<Application>;

  // UI Actions
  setCurrentOrg: (org: Organization | null) => void;
  setCurrentApp: (app: Application | null) => void;
  clearError: () => void;
  reset: () => void;
}

const CACHE_TTL = 15 * 60 * 1000;

export const useAppStore = create<AppState>((set, get) => ({
  organizations: [],
  applications: [],
  currentOrg: null,
  currentApp: null,
  loading: false,
  error: null,
  organizationsLoaded: false,
  organizationsCacheTime: 0,

  fetchOrganizations: async (force = false) => {
    const state = get();
    const now = Date.now();
    const isCacheValid = state.organizationsLoaded && (now - state.organizationsCacheTime < CACHE_TTL);

    if (isCacheValid && !force) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await getOrganizations() as { organizations: Organization[] };
      set({
        organizations: response.organizations || [],
        organizationsLoaded: true,
        organizationsCacheTime: now,
        loading: false
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch organizations';
      set({ error: errorMessage, loading: false });
      throw new Error(errorMessage);
    }
  },

  fetchOrganization: async (id: string): Promise<Organization> => {
    set({ loading: true, error: null });
    try {
      const org = await getOrganization(id) as Organization;
      set({ currentOrg: org, loading: false });
      return org;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to fetch organization';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  createOrg: async (data: { name: string; domain?: string }) => {
    set({ loading: true, error: null });
    try {
      const org = await createOrganization(data) as Organization;
      set((state) => ({
        organizations: [...state.organizations, org],
        organizationsLoaded: true,
        organizationsCacheTime: Date.now(),
        loading: false
      }));
      return org;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to create organization';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  updateOrg: async (id: string, data: { name?: string; domain?: string }) => {
    set({ loading: true, error: null });
    try {
      const org = await updateOrganization(id, data) as Organization;
      set((state) => ({
        organizations: state.organizations.map(o => o.id === id ? org : o),
        currentOrg: state.currentOrg?.id === id ? org : state.currentOrg,
        organizationsLoaded: true,
        organizationsCacheTime: Date.now(),
        loading: false
      }));
      return org;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to update organization';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  fetchApplications: async (orgId?: string) => {
    // Skip if no orgId provided (avoid unnecessary state updates)
    if (!orgId) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await getApplications(orgId) as { applications: Application[] };
      set({ applications: response.applications || [], loading: false });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch applications';
      set({ error: errorMessage, loading: false });
      throw new Error(errorMessage);
    }
  },

  fetchApplication: async (orgId: string, appId: string): Promise<Application> => {
    set({ loading: true, error: null });
    try {
      const app = await getApplication(orgId, appId) as Application;
      set({ currentApp: app, loading: false });
      return app;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to fetch application';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  createApp: async (orgId: string, data: {
    name: string;
    redirectUris: string[];
    appUrl?: string;
    logoUrl?: string;
    logUrl?: string;
    scope?: string[];
  }) => {
    set({ loading: true, error: null });
    try {
      const app = await createApplication(orgId, data) as Application;
      set((state) => ({
        applications: [...state.applications, app],
        loading: false
      }));
      return app;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to create application';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  updateApp: async (orgId: string, appId: string, data: {
    name?: string;
    redirectUris?: string[];
    appUrl?: string;
    logoUrl?: string;
    logUrl?: string;
    scope?: string[];
    isActive?: boolean;
  }) => {
    set({ loading: true, error: null });
    try {
      const app = await updateApplication(orgId, appId, data) as Application;
      set((state) => ({
        applications: state.applications.map(a => a.id === appId ? app : a),
        currentApp: state.currentApp?.id === appId ? app : state.currentApp,
        loading: false
      }));
      return app;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to update application';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  setCurrentOrg: (org: Organization | null) => {
    set({ currentOrg: org });
    // Clear applications when org changes
    if (!org) {
      set({ applications: [] });
    }
  },

  setCurrentApp: (app: Application | null) => {
    set({ currentApp: app });
  },

  clearError: () => set({ error: null }),

  reset: () => {
    set({
      organizations: [],
      applications: [],
      currentOrg: null,
      currentApp: null,
      loading: false,
      error: null,
      organizationsLoaded: false,
      organizationsCacheTime: 0,
    });
  },
}));
